import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Stepper, Card, Field, NavRow } from "@/components/OnboardKit";
import { ArrowRight, ShieldCheck, Landmark, User, Building2, Check, Award } from "lucide-react";
import { toast } from "sonner";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;
const STEPS = ["Investor Type", "Business Details", "Regulator / Body", "Owner Account", "Verified"];

const ICONS = {
  banker:                  Landmark,
  individual_financier:    User,
  institutional_financier: Building2,
};

/** Investor onboarding — 4-step (subtype + business + affiliations + owner).
 *  Investors can initiate projects and gain read-only financier views.
 */
export default function OnboardInvestor() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [subtypes, setSubtypes] = useState([]);
  const [subtype, setSubtype]   = useState(null);

  const [legal, setLegal]     = useState("");
  const [brand, setBrand]     = useState("");
  const [address, setAddress] = useState("");
  const [gstin, setGstin]     = useState("");
  const [pan, setPan]         = useState("");
  const [ownerName, setOwnerName]   = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerPwd, setOwnerPwd]     = useState("");
  const [sectionAffiliations, setSectionAffiliations] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/meta/investor-subtypes`).then(({ data }) => setSubtypes(data.subtypes || []));
  }, []);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/onboard/org`, {
        org_type: "investor",
        investor_subtype: subtype,
        legal_name: legal,
        brand_name: brand || legal,
        registered_address: address,
        gstin, pan,
        owner_name: ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_password: ownerPwd,
        section_affiliations: sectionAffiliations,
      });
      await setToken(data.access_token);
      await refresh();
      setStep(4);
      toast.success(`Welcome — ${data.organization.brand_name} is now on board`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="onboard-investor-page">
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Investor · Sign Up</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Back a Slate</h1>
      <Stepper steps={STEPS} step={step} />

      {step === 0 && (
        <Card title="What kind of investor are you?" testid="step-investor-subtype">
          <p className="text-xs text-white/55 mb-4">
            Pick the classification that best fits — it affects the KYC hints and the projects surface you see.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="subtype-grid">
            {subtypes.map((s) => {
              const Icon = ICONS[s.key] || Building2;
              const active = subtype === s.key;
              return (
                <button type="button" key={s.key} onClick={() => setSubtype(s.key)}
                  data-testid={`subtype-${s.key}`}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    active
                      ? "bg-white/[0.06] border-white/40"
                      : "bg-white/[0.02] border-white/10 hover:border-white/25"
                  }`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 bg-white/[0.05]">
                    <Icon size={16} />
                  </div>
                  <div className="font-display uppercase tracking-tight">{s.label}</div>
                  <div className="text-xs text-white/60 mt-1">{s.blurb}</div>
                  {active && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                      <Check size={12} /> Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <NavRow onNext={next} disabledNext={!subtype} />
        </Card>
      )}

      {step === 1 && (
        <Card title="Business Details" testid="step-inv-biz">
          <Field label="Legal Name *" tid="inv-legal">
            <Input value={legal} onChange={(e) => setLegal(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="inv-legal-input" />
          </Field>
          <Field label="Brand / Fund Name" tid="inv-brand">
            <Input value={brand} onChange={(e) => setBrand(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="inv-brand-input" />
          </Field>
          <Field label="Registered Address" tid="inv-address">
            <Textarea value={address} onChange={(e) => setAddress(e.target.value)}
              className="bg-black/40 border-white/15" data-testid="inv-address-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={subtype === "individual_financier" ? "PAN *" : "GSTIN"} tid="inv-gstin">
              <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                maxLength={15}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="inv-gstin-input" />
            </Field>
            <Field label="PAN" tid="inv-pan">
              <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                maxLength={10}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
                data-testid="inv-pan-input" />
            </Field>
          </div>
          <NavRow onBack={back} onNext={next} disabledNext={!legal} />
        </Card>
      )}

      {step === 2 && (
        <Card title="Regulator / Industry Body" testid="step-inv-affiliations">
          <div className="flex items-start gap-3 mb-3 p-3 rounded-lg bg-indigo-500/8 border border-indigo-500/30">
            <Award size={16} className="text-indigo-300 shrink-0 mt-0.5" />
            <div className="text-xs text-white/75 leading-relaxed">
              <span className="text-indigo-200 font-semibold">Optional but recommended.</span>{" "}
              Ticking your regulator (SEBI · RBI · IFSCA) or industry body
              (IVCA · AIF Alliance) drives which projects you see and unlocks the
              &quot;Regulated Financier&quot; chip on your public profile.
            </div>
          </div>
          <AffiliationsPicker
            section="investor"
            value={sectionAffiliations}
            onChange={setSectionAffiliations}
            testidPrefix="onboard-aff-investor"
          />
          <NavRow onBack={back} onNext={next} />
        </Card>
      )}

      {step === 3 && (
        <Card title="Owner Account" testid="step-inv-owner">
          <Field label="Full Name *" tid="inv-owner-name">
            <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="inv-owner-name-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email *" tid="inv-owner-email">
              <Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                className="h-11 bg-black/40 border-white/15" data-testid="inv-owner-email-input" />
            </Field>
            <Field label="Phone" tid="inv-owner-phone">
              <Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)}
                className="h-11 bg-black/40 border-white/15" data-testid="inv-owner-phone-input" />
            </Field>
          </div>
          <Field label="Password * (min 6 characters)" tid="inv-owner-pwd">
            <Input type="password" value={ownerPwd} onChange={(e) => setOwnerPwd(e.target.value)}
              className="h-11 bg-black/40 border-white/15" data-testid="inv-owner-pwd-input" />
          </Field>
          <NavRow onBack={back} onSubmit={submit} submitting={busy}
            submitLabel="Register as Investor"
            disabledNext={!ownerName || !ownerEmail || ownerPwd.length < 6} />
        </Card>
      )}

      {step === 4 && (
        <Card testid="step-inv-success">
          <div className="text-center py-6" data-testid="investor-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-tight mt-5">You&rsquo;re In</h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">
              You can now initiate projects, hire executing producers, and receive read-only
              financier views on projects you back.
            </p>
            <Button onClick={() => nav("/dashboard")}
              className="mt-7 btn-themed h-11 px-7 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
              data-testid="inv-go-dashboard">
              Open Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
